import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CategoryIcon from '@mui/icons-material/Category';
import MongoDBIcon from '../components/icons/MongoDBIcon';
import { DataAsset } from '../types/DataAsset';
import { API_BASE_URL } from '../services/api';
import dataAssetService from '../services/dataAssetService';
import lookupService, { Domain, Owner } from '../services/lookupService';
import {
  DataElementsTable,
  AtAGlanceSidebar,
  OverviewSection
} from '../components/DataAsset';

/**
 * Tab panel component for tabbed interface
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * DataAssetDetail Page Component
 * Displays rich detail view for a data asset (Customer Analytics style)
 */
const DataAssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<DataAsset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<DataAsset>>({});
  const [tagsInput, setTagsInput] = useState<string>('');
  const [policiesInput, setPoliciesInput] = useState<string>('');
  const [selectedCompliance, setSelectedCompliance] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Lookup data for dropdowns
  const [domains, setDomains] = useState<Domain[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAssetDetail(id);
    }
  }, [id]);

  const fetchAssetDetail = async (assetId: string) => {
    try {
      console.log('🔄 Fetching asset detail for:', assetId);
      console.log('📊 Current domain before refresh:', asset?.domain);
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/data-assets/${assetId}`);
      console.log('✅ Asset data refreshed:', response.data.data);
      console.log('📊 NEW domain from API:', response.data.data.domain);
      setAsset(response.data.data);
      console.log('📊 Domain after setState:', response.data.data.domain);
    } catch (err) {
      console.error('Error fetching asset detail:', err);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch asset details');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getApprovalStatusColor = (status?: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEditClick = async () => {
    if (asset) {
      setEditFormData({
        name: asset.name,
        description: asset.description,
        type: asset.type,
        domain: asset.domain,
        owner: asset.owner,
        status: asset.status,
        certification: asset.certification,
        approvalStatus: asset.approvalStatus,
        tags: asset.tags,
        governance: asset.governance || {},
        privacyClassification: asset.privacyClassification,
        sensitivityLevel: asset.sensitivityLevel,
        complianceFlags: asset.complianceFlags || []
      });
      // Set compliance frameworks
      setSelectedCompliance(asset.complianceFlags || []);
      // Convert tags array to comma-separated string for editing
      setTagsInput(Array.isArray(asset.tags) ? asset.tags.join(', ') : '');
      // Convert policies array of objects to comma-separated string for editing
      const policies = asset.governance?.policies;
      if (Array.isArray(policies)) {
        const policyNames = policies
          .map(p => (typeof p === 'string' ? p : p?.name))
          .filter((p): p is string => Boolean(p));
        setPoliciesInput(policyNames.join(', '));
      } else {
        setPoliciesInput('');
      }
      
      // Fetch lookup data for dropdowns
      setLoadingLookups(true);
      try {
        const lookupData = await lookupService.getAllLookups();
        setDomains(lookupData.domains);
        setOwners(lookupData.owners);
        setAssetTypes(lookupData.assetTypes);
        setStatuses(lookupData.statuses);
        setCertifications(lookupData.certifications);
      } catch (error) {
        console.error('Error fetching lookup data:', error);
      } finally {
        setLoadingLookups(false);
      }
      
      setEditDialogOpen(true);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditFormData({});
    setTagsInput('');
    setPoliciesInput('');
    setSelectedCompliance([]);
  };

  const handleEditSave = async () => {
    if (!id || !editFormData) {
      console.error('Cannot save: missing id or form data', { id, editFormData });
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Starting save process...');
      console.log('📝 Form data:', editFormData);
      
      // Convert tags string to array and policies string to array of objects
      const dataToSave = {
        ...editFormData,
        tags: tagsInput.split(',').map(t => t.trim()).filter(t => t),
        complianceFlags: selectedCompliance as ('GDPR' | 'CCPA' | 'PCI-DSS' | 'SOX' | 'HIPAA' | 'FERPA')[],
        governance: {
          ...editFormData.governance,
          policies: policiesInput
            .split(',')
            .map(p => p.trim())
            .filter(p => p)
            .map(policyName => ({ name: policyName, description: '', status: '' }))
        }
      };
      
      console.log('📤 Data to save:', dataToSave);
      await dataAssetService.updateDataAsset(id, dataToSave);
      console.log('✅ Save successful');
      
      setSnackbarMessage('Data asset updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setEditDialogOpen(false);
      
      // Refresh asset data
      await fetchAssetDetail(id);
    } catch (err) {
      console.error('Error updating asset:', err);
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to update asset');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGovernanceChange = (field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      governance: {
        ...prev.governance,
        [field]: value
      }
    }));
  };

  const handleComplianceToggle = (framework: string) => {
    setSelectedCompliance(prev =>
      prev.includes(framework)
        ? prev.filter(f => f !== framework)
        : [...prev, framework]
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading asset details...
        </Typography>
      </Container>
    );
  }

  if (error || !asset) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Asset not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: 3,
        bgcolor: 'white',
        minHeight: '100vh'
      }}
    >

      {/* Header */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderLeft: '4px solid #003366'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontFamily: 'Source Sans Pro, sans-serif',
              color: '#003366',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {asset.name}
            {asset.verificationBadge && (
              <span 
                style={{ marginLeft: '8px', fontSize: '0.8em', color: '#2E7D32' }}
                aria-label="Verified asset"
                role="img"
              >
                ✓
              </span>
            )}
          </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            sx={{
              bgcolor: '#003366',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#005a9c'
              },
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              }
            }}
            aria-label="Edit data asset"
          >
            Edit
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body1" sx={{ color: '#666' }}>
            {asset.assetType || asset.type}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }} aria-hidden="true">|</Typography>
          {asset.approvalStatus && (
            <Chip
              label={asset.approvalStatus}
              color={getApprovalStatusColor(asset.approvalStatus)}
              size="small"
              sx={{ fontWeight: 600 }}
              aria-label={`Approval status: ${asset.approvalStatus}`}
            />
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper 
        elevation={0}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 2,
          bgcolor: 'white'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Data asset information tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              minHeight: '48px',
              color: '#666',
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '-2px'
              },
              '&.Mui-selected': {
                color: '#003366'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#003366',
              height: '3px'
            }
          }}
        >
          <Tab 
            label="Summary" 
            id="asset-tab-0" 
            aria-controls="asset-tabpanel-0"
            aria-label="View asset summary"
          />
          <Tab 
            label="Data Elements" 
            id="asset-tab-1" 
            aria-controls="asset-tabpanel-1"
            aria-label="View data elements"
          />
          <Tab 
            label="Sample Data" 
            id="asset-tab-2" 
            aria-controls="asset-tabpanel-2"
            aria-label="View sample data"
          />
          <Tab 
            label="Quality" 
            id="asset-tab-3" 
            aria-controls="asset-tabpanel-3"
            aria-label="View quality metrics"
          />
          <Tab 
            label="History" 
            id="asset-tab-4" 
            aria-controls="asset-tabpanel-4"
            aria-label="View asset history"
          />
        </Tabs>
      </Paper>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid item xs={12} md={8}>
          {/* Summary Tab */}
          <TabPanel value={activeTab} index={0}>
            {/* Breadcrumbs - Unidatum Style */}
            <Breadcrumbs 
              aria-label="breadcrumb navigation" 
              sx={{ 
                mb: 3,
                '& .MuiBreadcrumbs-separator': {
                  color: '#71767A'
                }
              }}
              role="navigation"
            >
              {/* Database Level - MongoDB Atlas */}
              <Link
                color="inherit"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  color: '#0066CC',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  },
                  '&:focus': {
                    outline: '2px solid #003366',
                    outlineOffset: '2px'
                  }
                }}
                aria-label="Navigate to E-Unify database"
              >
                <MongoDBIcon sx={{ fontSize: 18 }} />
                E-UNIFY
              </Link>
              
              {/* Domain Level */}
              {asset.domain && (
                <Link
                  color="inherit"
                  href="/data-catalog"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/data-catalog');
                  }}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: '#0066CC',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    '&:focus': {
                      outline: '2px solid #003366',
                      outlineOffset: '2px'
                    }
                  }}
                  aria-label={`Navigate to ${asset.domain} domain`}
                >
                  <CategoryIcon sx={{ fontSize: 18 }} />
                  {asset.domain.toUpperCase().replace(/ /g, '_')}
                </Link>
              )}
              
              {/* Table/Asset Level */}
              <Typography 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#71767A',
                  fontSize: '14px',
                  fontWeight: 500
                }}
                aria-current="page"
              >
                <TableChartIcon sx={{ fontSize: 18 }} />
                {asset.name.toUpperCase().replace(/ /g, '_')}
              </Typography>
            </Breadcrumbs>
            
            <OverviewSection asset={asset} onUpdate={() => fetchAssetDetail(id!)} />
          </TabPanel>

          {/* Data Elements Tab */}
          <TabPanel value={activeTab} index={1}>
            <DataElementsTable 
              elements={asset.dataElements || []} 
              tableName={asset.name}
              domain={asset.domain}
              assetId={id}
              onUpdate={() => id && fetchAssetDetail(id)}
            />
          </TabPanel>

          {/* Sample Data Tab */}
          <TabPanel value={activeTab} index={2}>
            <Paper sx={{ p: 3 }}>
              {asset.sampleData?.enabled && asset.sampleData.rows.length > 0 ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Sample Data
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                          {Object.keys(asset.sampleData.rows[0]).map((key) => (
                            <th
                              key={key}
                              style={{
                                padding: '12px',
                                textAlign: 'left',
                                fontWeight: 600,
                                color: '#003366'
                              }}
                            >
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {asset.sampleData.rows.slice(0, 10).map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            {Object.values(row).map((value, colIdx) => (
                              <td
                                key={colIdx}
                                style={{
                                  padding: '12px',
                                  fontSize: '14px'
                                }}
                              >
                                {String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                  {asset.sampleData.lastRefreshed && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      Last refreshed: {new Date(asset.sampleData.lastRefreshed).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  No sample data available for this asset.
                </Alert>
              )}
            </Paper>
          </TabPanel>

          {/* Quality Tab */}
          <TabPanel value={activeTab} index={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quality Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="primary">
                      {asset.qualityMetrics?.completeness || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completeness
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="primary">
                      {asset.qualityMetrics?.accuracy || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accuracy
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="primary">
                      {asset.qualityMetrics?.consistency || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Consistency
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={activeTab} index={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Asset History
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {asset.createdAt ? new Date(asset.createdAt).toLocaleString() : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Last Modified: {asset.lastModified ? new Date(asset.lastModified).toLocaleString() : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Last Visited: {asset.lastVisitedAt ? new Date(asset.lastVisitedAt).toLocaleString() : 'Never'}
                </Typography>
              </Box>
            </Paper>
          </TabPanel>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <AtAGlanceSidebar asset={asset} onRatingChange={fetchAssetDetail} />
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-asset-dialog-title"
      >
        <DialogTitle 
          id="edit-asset-dialog-title"
          sx={{ 
            bgcolor: '#003366', 
            color: 'white'
          }}
        >
          Edit Data Asset
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Name"
                value={editFormData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                aria-label="Asset name"
                inputProps={{
                  'aria-required': 'true'
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Description"
                value={editFormData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={4}
                required
                aria-label="Asset description"
                inputProps={{
                  'aria-required': 'true'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  value={editFormData.type || ''}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  label="Type"
                  aria-label="Asset type"
                  aria-required="true"
                  disabled={loadingLookups}
                >
                  {assetTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="domain-label">Domain</InputLabel>
                <Select
                  labelId="domain-label"
                  value={editFormData.domain || ''}
                  onChange={(e) => handleFormChange('domain', e.target.value)}
                  label="Domain"
                  aria-label="Asset domain"
                  aria-required="true"
                  disabled={loadingLookups}
                >
                  {domains.map((domain) => (
                    <MenuItem key={domain._id} value={domain.name}>
                      {domain.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="owner-label">Owner</InputLabel>
                <Select
                  labelId="owner-label"
                  value={editFormData.owner || ''}
                  onChange={(e) => handleFormChange('owner', e.target.value)}
                  label="Owner"
                  aria-label="Asset owner"
                  aria-required="true"
                  disabled={loadingLookups}
                >
                  {owners.map((owner) => (
                    <MenuItem key={owner._id} value={owner.name}>
                      {owner.name} {owner.type && `(${owner.type})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={editFormData.status || ''}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                  aria-label="Asset status"
                  aria-required="true"
                  disabled={loadingLookups}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="certification-label">Certification</InputLabel>
                <Select
                  labelId="certification-label"
                  value={editFormData.certification || ''}
                  onChange={(e) => handleFormChange('certification', e.target.value)}
                  label="Certification"
                  aria-label="Asset certification status"
                  disabled={loadingLookups}
                >
                  {certifications.map((cert) => (
                    <MenuItem key={cert} value={cert}>
                      {cert.charAt(0).toUpperCase() + cert.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="approval-status-label">Approval Status</InputLabel>
                <Select
                  labelId="approval-status-label"
                  value={editFormData.approvalStatus || ''}
                  onChange={(e) => handleFormChange('approvalStatus', e.target.value)}
                  label="Approval Status"
                  aria-label="Asset approval status"
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Tags (comma-separated)"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                helperText="Enter tags separated by commas"
                aria-label="Asset tags"
                placeholder="e.g., sensitive, critical, real-time"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Stewards (comma-separated)"
                value={editFormData.stewards?.join(', ') || ''}
                onChange={(e) => handleFormChange('stewards', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                helperText="Enter steward names separated by commas"
                aria-label="Data stewards"
                placeholder="e.g., Amanda Martinez, Robert Johnson"
              />
            </Grid>

            {/* Governance Section */}
            <Grid item xs={12}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#003366',
                  mt: 2,
                  mb: 1,
                  borderBottom: '2px solid #003366',
                  pb: 0.5
                }}
              >
                Governance
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Compliance Status"
                value={editFormData.governance?.complianceStatus || ''}
                onChange={(e) => handleGovernanceChange('complianceStatus', e.target.value)}
                aria-label="Compliance status"
                placeholder="e.g., Compliant, Non-Compliant, Unknown"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Governance Owner"
                value={editFormData.governance?.owner || ''}
                onChange={(e) => handleGovernanceChange('owner', e.target.value)}
                aria-label="Governance owner"
                placeholder="e.g., Data Governance Team"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                Compliance Frameworks
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('GDPR')}
                      onChange={() => handleComplianceToggle('GDPR')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="GDPR"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('CCPA')}
                      onChange={() => handleComplianceToggle('CCPA')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="CCPA"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('PCI-DSS')}
                      onChange={() => handleComplianceToggle('PCI-DSS')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="PCI-DSS"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('SOX')}
                      onChange={() => handleComplianceToggle('SOX')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="SOX"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('HIPAA')}
                      onChange={() => handleComplianceToggle('HIPAA')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="HIPAA"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCompliance.includes('FERPA')}
                      onChange={() => handleComplianceToggle('FERPA')}
                      sx={{
                        color: '#003366',
                        '&.Mui-checked': { color: '#003366' }
                      }}
                    />
                  }
                  label="FERPA"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Policies (comma-separated)"
                value={policiesInput}
                onChange={(e) => setPoliciesInput(e.target.value)}
                helperText="Enter policy names separated by commas"
                aria-label="Governance policies"
                placeholder="e.g., Data Retention Policy, Privacy Policy"
              />
            </Grid>

            {/* Privacy & Security Section */}
            <Grid item xs={12}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#003366',
                  mt: 2,
                  mb: 1,
                  borderBottom: '2px solid #003366',
                  pb: 0.5
                }}
              >
                Privacy & Security
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="privacy-classification-label">Privacy Classification</InputLabel>
                <Select
                  labelId="privacy-classification-label"
                  value={editFormData.privacyClassification || ''}
                  onChange={(e) => handleFormChange('privacyClassification', e.target.value)}
                  label="Privacy Classification"
                  aria-label="Privacy classification level"
                >
                  <MenuItem value="Public">Public</MenuItem>
                  <MenuItem value="Internal">Internal</MenuItem>
                  <MenuItem value="Confidential">Confidential</MenuItem>
                  <MenuItem value="Restricted">Restricted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sensitivity-level-label">Sensitivity Level</InputLabel>
                <Select
                  labelId="sensitivity-level-label"
                  value={editFormData.sensitivityLevel || ''}
                  onChange={(e) => handleFormChange('sensitivityLevel', e.target.value)}
                  label="Sensitivity Level"
                  aria-label="Data sensitivity level"
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleEditClose}
            sx={{ 
              color: '#666',
              textTransform: 'none',
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              }
            }}
            aria-label="Cancel editing"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditSave}
            variant="contained"
            disabled={saving}
            sx={{
              bgcolor: '#003366',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#005a9c'
              },
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              }
            }}
            aria-label="Save changes"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          role="alert"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DataAssetDetail;
